<template>
  <div style="background-color: #fff;">
    <div class="margin-subpages talent-matrix-container">
      <!-- Header Controls -->
      <div class="header-controls">
        <div class="header-row header-row-primary">
          <div class="header-left">
            <el-select
              v-model="localSelectedTeam"
              :placeholder="$t('performance.talent_matrix.select_team')"
              filterable
              class="team-select"
              @change="handleTeamChange"
            >
              <el-option
                v-if="user.user_type !== 'leadership'"
                :label="`${$t('performance.talent_matrix.all_teams')} (${allTeamsCount})`"
                :value="''"
              />
              <el-option
                v-for="team in teamOptions"
                :key="team.key"
                :label="team.name"
                :value="team.key"
              />
            </el-select>

            <el-select
              v-model="localSelectedXAxis"
              :placeholder="$t('performance.talent_matrix.x_axis')"
              filterable
              class="axis-select"
              @change="handleXAxisChange"
            >
              <el-option
                v-for="opt in axisOptions"
                :key="'x-' + opt.key"
                :label="opt.label"
                :value="opt.key"
              />
            </el-select>

            <el-tooltip :content="$t('performance.talent_matrix.swap_axes')" placement="top">
              <button class="btn-swap" @click="swapAxes">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="16" height="16">
                  <path d="M7 16V4m0 0L3 8m4-4l4 4M17 8v12m0 0l4-4m-4 4l-4-4"/>
                </svg>
              </button>
            </el-tooltip>

            <el-select
              v-model="localSelectedYAxis"
              :placeholder="$t('performance.talent_matrix.y_axis')"
              filterable
              class="axis-select"
              @change="handleYAxisChange"
            >
              <el-option
                v-for="opt in axisOptions"
                :key="'y-' + opt.key"
                :label="opt.label"
                :value="opt.key"
              />
            </el-select>
          </div>

          <div class="header-right">
            <el-select
              v-model="localColorBy"
              class="color-by-select"
              @change="handleColorByChange"
            >
              <el-option :label="$t('performance.talent_matrix.color_by_talent_type')" value="talent_type" />
              <el-option :label="$t('performance.talent_matrix.color_by_team')" value="team" />
              <el-option :label="$t('performance.talent_matrix.no_coloring')" value="none" />
            </el-select>

            <el-tooltip :content="$t('performance.talent_matrix.export_csv')" placement="top">
              <svg
                @click="exportCSV"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                class="icon-export"
              >
                <path
                  fill="currentColor"
                  d="m12 16l-5-5l1.4-1.45l2.6 2.6V4h2v8.15l2.6-2.6L17 11zm-6 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z"
                />
              </svg>
            </el-tooltip>
          </div>
        </div>

        <!-- Sub-teams toggle hidden — always on by default, can restore later -->
        <!-- <div class="header-row header-row-filters">
          <el-tooltip content="When enabled, includes employees from all child teams" placement="top">
            <div class="toggle-wrapper">
              <el-switch
                v-model="includeSubteams"
                active-color="#52c1c7"
                inactive-color="#dcdfe6"
              />
              <span class="toggle-label">Include Sub-teams</span>
            </div>
          </el-tooltip>
        </div> -->
      </div>

      <!-- Dimension Info Bar -->
      <div class="dimension-info-bar">
        <div class="dimension-info-item">
          <div class="dimension-info-header">
            <span class="dimension-info-axis">{{ $t('performance.talent_matrix.x_axis') }}</span>
            <span class="dimension-info-label">{{ xAxisLabel }}</span>
            <span v-if="xAxisConfig" class="dimension-info-polarity" :class="xAxisConfig.highIsGood ? 'polarity-positive' : 'polarity-negative'">
              {{ xAxisConfig.highIsGood ? $t('performance.talent_matrix.higher_is_better') : $t('performance.talent_matrix.lower_is_better') }}
            </span>
          </div>
          <div v-if="xAxisConfig && xAxisConfig.description" class="dimension-info-desc">{{ xAxisConfig.description }}</div>
          <div v-if="xAxisConfig && xAxisConfig.dataNote" class="dimension-info-data">{{ xAxisConfig.dataNote }}</div>
        </div>
        <div class="dimension-info-divider"></div>
        <div class="dimension-info-item">
          <div class="dimension-info-header">
            <span class="dimension-info-axis">{{ $t('performance.talent_matrix.y_axis') }}</span>
            <span class="dimension-info-label">{{ yAxisLabel }}</span>
            <span v-if="yAxisConfig" class="dimension-info-polarity" :class="yAxisConfig.highIsGood ? 'polarity-positive' : 'polarity-negative'">
              {{ yAxisConfig.highIsGood ? $t('performance.talent_matrix.higher_is_better') : $t('performance.talent_matrix.lower_is_better') }}
            </span>
          </div>
          <div v-if="yAxisConfig && yAxisConfig.description" class="dimension-info-desc">{{ yAxisConfig.description }}</div>
          <div v-if="yAxisConfig && yAxisConfig.dataNote" class="dimension-info-data">{{ yAxisConfig.dataNote }}</div>
        </div>
      </div>

      <!-- Chart Area -->
      <div class="chart-wrapper">
        <div v-if="showMatrixLoadingOverlay" class="matrix-loading-overlay">
          <div class="matrix-loader">
            <div class="matrix-cell cell-tl"></div>
            <div class="matrix-cell cell-tr"></div>
            <div class="matrix-cell cell-bl"></div>
            <div class="matrix-cell cell-br"></div>
          </div>
        </div>
        <canvas ref="talentMatrixChart"></canvas>

        <!-- Custom Tooltip -->
        <div
          class="chart-tooltip"
          :class="{ visible: tooltipVisible }"
          :style="tooltipStyle"
        >
          <template v-if="tooltipData">
            <div class="tooltip-header">
              <div class="tooltip-avatar" :class="[ blurMode ? 'blurelement' : '' ]">
                <img v-if="tooltipData.pic" :src="tooltipData.pic" />
                <span v-else>{{ getInitials(tooltipData.name) }}</span>
              </div>
              <div>
                <div class="tooltip-name" :class="[ blurMode ? 'blurelement' : '' ]">{{ tooltipData.name }}</div>
                <div class="tooltip-team" :class="[ blurMode ? 'blurelement' : '' ]">{{ tooltipData.team_name }} <span v-if="tooltipData.talent_type">&middot; {{ tooltipData.talent_type }}</span></div>
              </div>
            </div>
            <div class="tooltip-metrics">
              <div class="tooltip-metric">
                <span class="tooltip-metric-label">{{ xAxisLabel }}</span>
                <span class="tooltip-metric-val">{{ tooltipData.xVal }}</span>
              </div>
              <div class="tooltip-metric">
                <span class="tooltip-metric-label">{{ yAxisLabel }}</span>
                <span class="tooltip-metric-val">{{ tooltipData.yVal }}</span>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Legend -->
      <div class="chart-legend" v-if="selectedMetricDataReady() && legendItems.length > 0">
        <div
          v-for="item in legendItems"
          :key="item.label"
          class="legend-item"
        >
          <span class="legend-dot" :style="{ background: item.color }"></span>
          {{ item.label }}
        </div>
      </div>

      <!-- Quadrant Summary Pills -->
      <div v-if="selectedMetricDataReady()" class="quadrant-summary">
        <div :class="['q-pill', quadrantPillClass('tr')]">
          <span class="q-count">{{ quadrantCounts.tr }}</span>
          {{ quadrantLabels.tr }}
        </div>
        <div :class="['q-pill', quadrantPillClass('tl')]">
          <span class="q-count">{{ quadrantCounts.tl }}</span>
          {{ quadrantLabels.tl }}
        </div>
        <div :class="['q-pill', quadrantPillClass('br')]">
          <span class="q-count">{{ quadrantCounts.br }}</span>
          {{ quadrantLabels.br }}
        </div>
        <div :class="['q-pill', quadrantPillClass('bl')]">
          <span class="q-count">{{ quadrantCounts.bl }}</span>
          {{ quadrantLabels.bl }}
        </div>
      </div>

      <!-- Data Table -->
      <div v-if="selectedMetricDataReady()" class="table-header-row">
        <span class="table-title">{{ $t('performance.talent_matrix.employee_details') }}</span>
        <span class="table-count">{{ tableData.length }} {{ $t('performance.talent_matrix.employees') }}</span>
      </div>

      <el-table
        v-if="selectedMetricDataReady()"
        :data="tableData"
        class="talent-table"
        :row-key="getRowKey"
        :row-class-name="'talent-table-row'"
        :header-cell-style="tableHeaderCellStyle"
        :cell-style="tableCellStyle"
        @row-click="handleRowClick"
        @sort-change="handleSortChange"
      >
        <el-table-column prop="name" :label="$t('performance.talent_matrix.employee')" sortable="custom" width="220" fixed>
          <template slot-scope="scope">
            <div class="emp-cell">
              <div class="emp-avatar" :class="[ blurMode ? 'blurelement' : '' ]">
                <img v-if="scope.row.pic || scope.row.profilepic_small" :src="scope.row.pic || scope.row.profilepic_small" loading="lazy" decoding="async" />
                <span v-else>{{ getInitials(scope.row.name) }}</span>
              </div>
              <div class="emp-info">
                <div class="emp-name" :class="[ blurMode ? 'blurelement' : '' ]">{{ scope.row.name }}</div>
                <div class="emp-team-small" :class="[ blurMode ? 'blurelement' : '' ]">{{ scope.row.team_name }}</div>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column :prop="'xVal'" :label="xAxisLabel" sortable="custom" width="130" align="center">
          <template slot-scope="scope">
            <span class="metric-pill" :style="scope.row.xPillStyle">{{ getMetricDisplayValue(scope.row.xVal, selectedXAxis) }}</span>
          </template>
        </el-table-column>
        <el-table-column :prop="'yVal'" :label="yAxisLabel" sortable="custom" width="130" align="center">
          <template slot-scope="scope">
            <span class="metric-pill" :style="scope.row.yPillStyle">{{ getMetricDisplayValue(scope.row.yVal, selectedYAxis) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="network_type" :label="$t('performance.talent_matrix.network')" sortable="custom" width="150" align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.network_type" class="network-badge" :style="scope.row.networkBadgeStyle">
              <span class="badge-dot" :style="{ background: scope.row.networkColor }"></span>
              {{ scope.row.network_type }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="talent_type" :label="$t('performance.talent_matrix.talent_type')" sortable="custom" width="160" align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.talent_type" class="talent-badge" :style="scope.row.talentBadgeStyle">
              <span class="badge-dot" :style="{ background: scope.row.talentColor }"></span>
              {{ scope.row.talent_type }}
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="quadrant" :label="$t('performance.talent_matrix.quadrant')" width="250" align="center">
          <template slot-scope="scope">
            <span class="quadrant-tag" :class="scope.row.quadrantClass">{{ scope.row.quadrant }}</span>
          </template>
        </el-table-column>
      </el-table>

      <!-- Detail Drawer -->
      <el-drawer
        :visible.sync="drawerVisible"
        direction="rtl"
        size="420px"
        :show-close="true"
        :with-header="false"
      >
        <div v-if="drawerEmployee" class="drawer-content">
          <!-- Profile Header -->
          <div class="drawer-profile">
            <div class="drawer-profile-top">
              <div class="drawer-avatar-ring" :class="[ blurMode ? 'blurelement' : '' ]">
                <div class="drawer-avatar">
                  <img v-if="drawerEmployee.pic || drawerEmployee.profilepic_small" :src="drawerEmployee.pic || drawerEmployee.profilepic_small" />
                  <span v-else>{{ getInitials(drawerEmployee.name) }}</span>
                </div>
              </div>
              <div class="drawer-profile-info">
                <div class="drawer-name" :class="[ blurMode ? 'blurelement' : '' ]">{{ drawerEmployee.name }}</div>
                <div class="drawer-meta" :class="[ blurMode ? 'blurelement' : '' ]">{{ drawerEmployee.team_name }}</div>
                <div class="drawer-tags">
                  <span v-if="drawerEmployee.talent_type" class="talent-badge" :style="getTalentBadgeStyle(drawerEmployee.talent_type)">
                    <span class="badge-dot" :style="{ background: getTalentColor(drawerEmployee.talent_type) }"></span>
                    {{ drawerEmployee.talent_type }}
                  </span>
                  <span class="quadrant-tag" :class="drawerEmployee.quadrantClass">
                    {{ drawerEmployee.quadrant }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Engagement & Well-being -->
          <div class="drawer-section">
            <div class="drawer-section-title">{{ $t('performance.talent_matrix.engagement_wellbeing') }}</div>
            <div class="metric-row" v-for="m in [
              { label: $t('performance.talent_matrix.happiness_score'), key: 'healthbar', max: 100, suffix: '' },
              { label: $t('performance.talent_matrix.engagement_score'), key: 'computed_engagement', max: 100, suffix: '' },
              { label: $t('performance.talent_matrix.flight_risk'), key: 'computed_flight_risk', max: 100, suffix: '%', invert: true },
            ]" :key="'ew-' + m.key">
              <span class="metric-label">{{ m.label }}</span>
              <div class="metric-bar-wrap">
                <div class="metric-bar-track">
                  <div class="metric-bar-fill" :style="{ width: getBarWidth(drawerEmployee, m.key, m.max) + '%', background: getScoreColor(getMetricValue(drawerEmployee, m.key), m.max, m.invert) }"></div>
                </div>
                <span class="metric-value">{{ getMetricDisplayForEmployee(drawerEmployee, m.key, m.suffix) }}</span>
              </div>
            </div>
          </div>

          <!-- Performance & Growth -->
          <div class="drawer-section">
            <div class="drawer-section-title">{{ $t('performance.talent_matrix.performance_growth') }}</div>
            <div class="metric-row" v-for="m in [
              { label: $t('performance.talent_matrix.impact_rating'), key: 'impact_rating', max: 150, suffix: '' },
              { label: $t('performance.talent_matrix.performance_score'), key: 'computed_performance', max: 100, suffix: '%' },
              { label: $t('performance.talent_matrix.focus_score'), key: 'computed_focus', max: 100, suffix: '' },
              { label: $t('performance.talent_matrix.goal_alignment'), key: 'computed_goal_alignment', max: 100, suffix: '%' },
            ]" :key="'pg-' + m.key">
              <span class="metric-label">{{ m.label }}</span>
              <div class="metric-bar-wrap">
                <div class="metric-bar-track">
                  <div class="metric-bar-fill" :style="{ width: getBarWidth(drawerEmployee, m.key, m.max) + '%', background: getScoreColor(getMetricValue(drawerEmployee, m.key), m.max) }"></div>
                </div>
                <span class="metric-value">{{ getMetricDisplayForEmployee(drawerEmployee, m.key, m.suffix) }}</span>
              </div>
            </div>
          </div>

          <!-- Recognition & Trust -->
          <div class="drawer-section">
            <div class="drawer-section-title">{{ $t('performance.talent_matrix.recognition_trust') }}</div>
            <div class="metric-row" v-for="m in [
              { label: $t('performance.talent_matrix.trust_score'), key: 'computed_trust', max: 100, suffix: '' },
              { label: $t('performance.talent_matrix.peer_trust_count'), key: 'computed_peer_trust', max: 15, suffix: '' },
              { label: $t('performance.talent_matrix.giving_recognition'), key: 'computed_giving_recognition', max: 100, suffix: '' },
              { label: $t('performance.talent_matrix.receiving_recognition'), key: 'computed_receiving_recognition', max: 100, suffix: '' },
              { label: $t('performance.talent_matrix.medals'), key: 'medalCount', max: 30, suffix: '' },
            ]" :key="'rt-' + m.key">
              <span class="metric-label">{{ m.label }}</span>
              <div class="metric-bar-wrap">
                <div class="metric-bar-track">
                  <div class="metric-bar-fill" :style="{ width: getBarWidth(drawerEmployee, m.key, m.max) + '%', background: getScoreColor(getMetricValue(drawerEmployee, m.key), m.max) }"></div>
                </div>
                <span class="metric-value">{{ getMetricDisplayForEmployee(drawerEmployee, m.key, m.suffix) }}</span>
              </div>
            </div>
          </div>

          <!-- Power Skills (EQ) -->
          <div class="drawer-section">
            <div class="drawer-section-title">{{ $t('performance.talent_matrix.power_skills_eq') }}</div>
            <div v-for="ps in powerSkillKeys" :key="ps.key" class="metric-row">
              <span class="metric-label">{{ ps.label }}</span>
              <div class="metric-bar-wrap">
                <div class="metric-bar-track">
                  <div class="metric-bar-fill" :style="{ width: getBarWidth(drawerEmployee, ps.key, 10) + '%', background: getScoreColor(getMetricValue(drawerEmployee, ps.key), 10) }"></div>
                </div>
                <span class="metric-value">{{ getMetricDisplayForEmployee(drawerEmployee, ps.key, '') }}</span>
              </div>
            </div>
          </div>
        </div>
      </el-drawer>
    </div>
  </div>
</template>

<script>
import Chart from 'chart.js/auto'
import { mapGetters } from 'vuex'
import { AXIS_OPTIONS } from '@/store/talent-matrix'
import { getTeamByCompany } from '@/store/core/cacheService'

// Unique colors per talent type based on grid position
// Grid: X = Organizational Contribution (low→high), Y = Individual Performance (low→high)
// Colors progress from red (bottom-left) through amber/teal to green (top-right)
const TALENT_COLORS = {
  // Row 4 (highest individual performance) — greens
  'Superstar':           '#1B5E20',  // top-right: best overall
  'High Performer':      '#2E7D32',  // high perf, high-mid contrib
  'Activated':           '#558B2F',  // high perf, low-mid contrib
  'Untapped Potential':  '#9E9D24',  // high perf, low contrib — potential not leveraged

  // Row 3 — teal/green spectrum
  'Distinguished Talent':'#00695C',  // high-mid perf, highest contrib
  'Respected Expert':    '#00897B',  // high-mid perf, high contrib
  'Key Player':          '#26A69A',  // high-mid perf, low-mid contrib
  'Valued Contributor':  '#4DB6AC',  // high-mid perf, low contrib

  // Row 2 — blue/purple spectrum
  'Solid Performer':     '#1565C0',  // mid perf, highest contrib
  'Core Player':         '#1976D2',  // mid perf, high contrib
  'Engaged Contributor': '#42A5F5',  // mid perf, low-mid contrib
  'Community Pillar':    '#5C6BC0',  // mid perf, low contrib — strong socially

  // Row 1 (lowest individual performance) — amber/orange/red
  'High Potential':      '#F57F17',  // low perf, highest contrib — promising
  'Emerging Talent':     '#FF8F00',  // low perf, high contrib — developing
  'Rising Star':         '#FF6D00',  // low perf, low-mid contrib — early stage
  'Up-and-Coming':       '#E65100',  // low perf, low-mid contrib

  // Bottom row edge
  'Unsung Talent':       '#5D4037',  // low perf, highest contrib — deep brown, underrecognized
  'Crowd Favorite':      '#AB47BC',  // low perf, high contrib — purple, liked but low output
  'Contributor':         '#78909C',  // low perf, low-mid contrib — blue-gray, steady but basic
  'Latent Potential':    '#C62828',  // lowest — needs intervention

  // Legacy names
  'Up-and-coming':       '#E65100',
  'High Contributor':    '#26A69A',
  'Developing':          '#FF8F00',
  'Hidden Talent':       '#5D4037',
  'Supported':           '#AB47BC',
  'Underutilized':       '#9E9D24',
  'Marginalized':        '#C62828',
}

const TEAM_COLORS = ['#6C30CC', '#3AC47D', '#F7B924', '#2196F3', '#FF6B6B', '#26A69A', '#FF9800', '#9C27B0', '#607D8B', '#795548']
const GOAL_AXIS_KEYS = ['owned_key_results', 'kr_progress', 'kr_health']
const QUADRANT_BG_COLORS = { 3: 'rgba(183, 28, 28, 0.08)', 2: 'rgba(58, 196, 125, 0.08)', 1: 'rgba(247, 185, 36, 0.06)', 0: 'rgba(204, 48, 48, 0.06)' }
const QUADRANT_LABEL_COLORS = { 3: 'rgba(183, 28, 28, 0.5)', 2: 'rgba(26, 138, 74, 0.5)', 1: 'rgba(184, 134, 11, 0.45)', 0: 'rgba(204, 48, 48, 0.45)' }

const QUADRANT_PLUGIN = {
  id: 'quadrantBackgrounds',
  beforeDraw: function (chart, args, options) {
    options = options || {}
    var ctx = chart.ctx
    var chartArea = chart.chartArea
    if (!chartArea) return

    var xScale = chart.scales.x
    var yScale = chart.scales.y
    var xMid = typeof options.xMid === 'number' ? options.xMid : 50
    var yMid = typeof options.yMid === 'number' ? options.yMid : 50
    var midXPx = xScale.getPixelForValue(xMid)
    var midYPx = yScale.getPixelForValue(yMid)
    var labels = options.labels || {}
    var sentiments = options.sentiments || {}

    midXPx = Math.max(chartArea.left, Math.min(chartArea.right, midXPx))
    midYPx = Math.max(chartArea.top, Math.min(chartArea.bottom, midYPx))

    var quadrants = [
      { key: 'tl', x1: chartArea.left, y1: chartArea.top, x2: midXPx, y2: midYPx },
      { key: 'tr', x1: midXPx, y1: chartArea.top, x2: chartArea.right, y2: midYPx },
      { key: 'bl', x1: chartArea.left, y1: midYPx, x2: midXPx, y2: chartArea.bottom },
      { key: 'br', x1: midXPx, y1: midYPx, x2: chartArea.right, y2: chartArea.bottom },
    ]

    quadrants.forEach(function (q) {
      var sentiment = sentiments[q.key] || 0
      ctx.fillStyle = QUADRANT_BG_COLORS[sentiment] || QUADRANT_BG_COLORS[0]
      ctx.fillRect(q.x1, q.y1, q.x2 - q.x1, q.y2 - q.y1)
    })

    ctx.save()
    ctx.setLineDash([4, 4])
    ctx.lineWidth = 1
    ctx.strokeStyle = 'rgba(108, 48, 204, 0.18)'
    ctx.beginPath()
    ctx.moveTo(midXPx, chartArea.top)
    ctx.lineTo(midXPx, chartArea.bottom)
    ctx.stroke()
    ctx.beginPath()
    ctx.moveTo(chartArea.left, midYPx)
    ctx.lineTo(chartArea.right, midYPx)
    ctx.stroke()
    ctx.restore()

    ctx.save()
    ctx.font = '600 10px Lato, sans-serif'
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    quadrants.forEach(function (q) {
      var label = labels[q.key] || ''
      if (!label) return
      var sentiment = sentiments[q.key] || 0
      ctx.fillStyle = QUADRANT_LABEL_COLORS[sentiment] || QUADRANT_LABEL_COLORS[0]
      ctx.fillText(label.toUpperCase(), (q.x1 + q.x2) / 2, (q.y1 + q.y2) / 2)
    })
    ctx.restore()
  },
}

function roundMetric(value) {
  return Math.round(value * 10) / 10
}

function getKrOwnerValue(kr, email) {
  if (kr && kr.mode === 'individual' && kr.owner_progress) {
    return Number(kr.owner_progress[email] || 0)
  }

  return Number((kr && kr.current_value) || 0)
}

function getKrOwnerProgress(kr, email) {
  if (!kr || kr.target_value === kr.start_value) return 0

  var currentValue = getKrOwnerValue(kr, email)
  var raw = ((currentValue - kr.start_value) / (kr.target_value - kr.start_value)) * 100
  return Math.min(Math.max(raw, 0), 100)
}

function getKrHealthStatus(progress) {
  if (progress >= 100) return 'completed'
  if (progress >= 60) return 'on_track'
  if (progress >= 30) return 'at_risk'
  return 'off_track'
}

export default {
  name: 'TalentMatrix',

  data() {
    return {
      chart: null,
      teamOptions: [],
      allTeamsCount: 0,
      drawerVisible: false,
      drawerEmployee: null,
      searchKeyword: '',
      includeSubteams: true,
      tooltipVisible: false,
      tooltipData: null,
      tooltipStyle: {},
      localSelectedTeam: '',
      localSelectedXAxis: 'impact_rating',
      localSelectedYAxis: 'healthbar',
      localColorBy: 'talent_type',
      sortProp: null,
      sortOrder: null,
      tableHeaderCellStyle: {
        background: '#fafafa',
        borderBottom: '1px solid #e0e0e0',
        color: '#909399',
        fontSize: '12px',
        fontWeight: '600',
        padding: '8px 0',
        whiteSpace: 'nowrap',
      },
      tableCellStyle: {
        padding: '12px 0',
        borderBottom: '1px solid #f0f0f0',
      },
      baseLoadInFlight: false,
      optionalMetricLoadInFlight: false,
      goalMetricLoadInFlight: false,
      goalMetricsLoadedCompany: '',
      pendingBaseReload: false,
      chartUpdateFrame: null,
      chartUpdateFrameType: null,
      chartUpdateQueued: false,
    }
  },

  computed: {
    ...mapGetters('authen', { user: 'user' }),
    ...mapGetters('talentMatrix', ['loading']),
    blurMode() {
      return this.$store.getters['demomode/blurMode']
    },

    selectedXAxis() { return this.localSelectedXAxis },
    selectedYAxis() { return this.localSelectedYAxis },
    selectedTeam() { return this.localSelectedTeam },
    colorBy() { return this.localColorBy },

    axisOptions() { return AXIS_OPTIONS },

    xAxisConfig() { return AXIS_OPTIONS.find(o => o.key === this.selectedXAxis) },
    yAxisConfig() { return AXIS_OPTIONS.find(o => o.key === this.selectedYAxis) },
    xAxisLabel() { return this.xAxisConfig ? this.xAxisConfig.label : '' },
    yAxisLabel() { return this.yAxisConfig ? this.yAxisConfig.label : '' },
    xAxisRange() { return this.computeAxisRange(this.selectedXAxis, this.baseMatrixRows.map(function (row) { return row.xVal })) },
    yAxisRange() { return this.computeAxisRange(this.selectedYAxis, this.baseMatrixRows.map(function (row) { return row.yVal })) },

    allEmployees() {
      return this.$store.state.people.starperformall || []
    },

    compositeScores() {
      return this.$store.state.talentMatrix ? (this.$store.state.talentMatrix.compositeScores || {}) : {}
    },

    allGoalsFlat() {
      return this.$store.getters['goals/allGoalsFlat'] || []
    },

    goalMetricsByEmail() {
      var metrics = {}

      this.allGoalsFlat.forEach(function (goal) {
        var keyResults = goal && goal.key_results ? goal.key_results : []
        keyResults.forEach(function (kr) {
          var ownerEmails = Array.isArray(kr.owner_emails) ? Array.from(new Set(kr.owner_emails)) : []
          ownerEmails.forEach(function (email) {
            if (!email) return

            if (!metrics[email]) {
              metrics[email] = {
                owned_key_results: 0,
                progressSum: 0,
                healthyCount: 0,
              }
            }

            var progress = getKrOwnerProgress(kr, email)
            var healthStatus = getKrHealthStatus(progress)

            metrics[email].owned_key_results += 1
            metrics[email].progressSum += progress
            if (healthStatus === 'on_track' || healthStatus === 'completed') {
              metrics[email].healthyCount += 1
            }
          })
        })
      })

      Object.keys(metrics).forEach(function (email) {
        var ownedCount = metrics[email].owned_key_results || 0
        metrics[email].kr_progress = ownedCount > 0 ? roundMetric(metrics[email].progressSum / ownedCount) : 0
        metrics[email].kr_health = ownedCount > 0 ? roundMetric((metrics[email].healthyCount * 100) / ownedCount) : 0
      })

      return metrics
    },

    showMatrixLoadingOverlay() {
      return this.loading ||
        this.baseLoadInFlight ||
        this.goalMetricLoadInFlight ||
        this.optionalMetricLoadInFlight ||
        ((this.hasAnyCompositeMetricSelected() || this.hasAnyGoalMetricSelected()) && !this.selectedMetricDataReady())
    },

    childrenByParent() {
      var index = {}
      this.teamOptions.forEach(function (team) {
        if (team.parent) {
          if (!index[team.parent]) index[team.parent] = []
          index[team.parent].push(team.key)
        }
      })
      return index
    },

    selectedTeamDescendants() {
      if (!this.localSelectedTeam) return null
      var descendants = new Set([this.localSelectedTeam])
      if (this.includeSubteams) {
        var queue = [this.localSelectedTeam]
        var childrenIndex = this.childrenByParent
        while (queue.length > 0) {
          var parentId = queue.shift()
          var children = childrenIndex[parentId] || []
          for (var i = 0; i < children.length; i++) {
            if (!descendants.has(children[i])) {
              descendants.add(children[i])
              queue.push(children[i])
            }
          }
        }
      }
      return descendants
    },

    filteredEmployees() {
      var emps = this.allEmployees
      var teamSet = this.selectedTeamDescendants
      if (teamSet) {
        emps = emps.filter(function (e) { return teamSet.has(e.team) })
      }
      if (this.searchKeyword) {
        var q = this.searchKeyword.toLowerCase()
        emps = emps.filter(function (e) {
          return (e.fullname || '').toLowerCase().indexOf(q) !== -1 ||
            (e.team_name || '').toLowerCase().indexOf(q) !== -1 ||
            (e.talent || '').toLowerCase().indexOf(q) !== -1
        })
      }
      return emps
    },

    baseMatrixRows() {
      if (!this.selectedMetricDataReady()) return []

      var self = this
      var xKey = this.selectedXAxis
      var yKey = this.selectedYAxis

      return this.filteredEmployees.map(function (emp, index) {
        var rowKey = self.getRowKeyFromEmployee(emp, index)
        var xVal = self.getMetricValue(emp, xKey)
        var yVal = self.getMetricValue(emp, yKey)
        if (self.isMissingMetricValue(xVal) || self.isMissingMetricValue(yVal)) return null

        return Object.assign({}, emp, {
          rowKey: rowKey,
          name: self.getDisplayName(emp),
          team_name: emp.team_name || '',
          talent_type: emp.talent || '',
          network_type: emp.network_type || '',
          xVal: xVal,
          yVal: yVal,
          chartX: self.getChartValueFromValue(xVal, xKey, rowKey),
          chartY: self.getChartValueFromValue(yVal, yKey, rowKey),
        })
      }).filter(function (row) { return !!row })
    },

    matrixRows() {
      var self = this
      var xRange = this.xAxisRange
      var yRange = this.yAxisRange
      var labels = this.quadrantLabels

      return this.baseMatrixRows.map(function (row) {
        var quadrantKey = self.getQuadrantKeyFromValues(row.xVal, row.yVal, xRange, yRange)
        var talentColor = self.getTalentColor(row.talent_type)
        var networkColor = self.getNetworkColor(row.network_type)

        return Object.assign({}, row, {
          quadrantKey: quadrantKey,
          quadrant: labels[quadrantKey] || '',
          quadrantClass: self.quadrantTagClass(quadrantKey),
          xPillStyle: self.getMetricPillStyle(row.xVal, self.selectedXAxis),
          yPillStyle: self.getMetricPillStyle(row.yVal, self.selectedYAxis),
          talentColor: talentColor,
          talentBadgeStyle: self.badgeStyle(talentColor),
          networkColor: networkColor,
          networkBadgeStyle: self.badgeStyle(networkColor),
        })
      })
    },

    tableData() {
      var data = this.matrixRows.slice()

      if (this.sortProp && this.sortOrder) {
        var prop = this.sortProp
        var asc = this.sortOrder === 'ascending' ? 1 : -1
        data.sort(function (a, b) {
          var va = a[prop]
          var vb = b[prop]
          // Coerce to numbers for numeric columns (xVal, yVal, or any parseable value)
          var na = parseFloat(va)
          var nb = parseFloat(vb)
          if (!isNaN(na) && !isNaN(nb)) return (na - nb) * asc
          return String(va || '').localeCompare(String(vb || '')) * asc
        })
      }

      return data
    },

    quadrantCounts() {
      var counts = { tr: 0, tl: 0, br: 0, bl: 0 }
      this.matrixRows.forEach(function (row) {
        if (counts[row.quadrantKey] !== undefined) counts[row.quadrantKey]++
      })
      return counts
    },

    quadrantLabels() {
      var x = this.selectedXAxis
      var y = this.selectedYAxis
      var LABELS = {
        'impact_rating-healthbar': {
          tl: 'Low Impact, Happy',
          tr: 'High Impact, Happy',
          bl: 'Low Impact, Unhappy',
          br: 'High Impact, Unhappy',
        },
        'impact_rating-computed_flight_risk': {
          tl: 'Underperforming at Risk',
          tr: 'Stars at Risk',
          bl: 'Steady Contributors',
          br: 'Stable Stars',
        },
        'impact_rating-computed_engagement': {
          tl: 'Low Impact, Engaged',
          tr: 'High Impact, Engaged',
          bl: 'Low Impact, Disengaged',
          br: 'High Impact, Disengaged',
        },
        'impact_rating-computed_trust': {
          tl: 'Low Impact, Trusted',
          tr: 'High Impact, Trusted',
          bl: 'Low Impact, Low Trust',
          br: 'High Impact, Low Trust',
        },
        'impact_rating-computed_performance': {
          tl: 'Low Impact, High Review',
          tr: 'High Impact, High Review',
          bl: 'Low Impact, Low Review',
          br: 'High Impact, Low Review',
        },
        'computed_focus-computed_goal_alignment': {
          tl: 'Low Focus, Aligned',
          tr: 'Focused & Aligned',
          bl: 'Low Focus, Misaligned',
          br: 'Focused, Misaligned',
        },
        'computed_giving_recognition-computed_receiving_recognition': {
          tl: 'Low Giver, Recognized',
          tr: 'Active Giver, Recognized',
          bl: 'Low Giver, Unrecognized',
          br: 'Active Giver, Unrecognized',
        },
      }
      var specific = LABELS[x + '-' + y]
      if (specific) return specific
      // Generate short labels by removing common suffixes
      var xShort = this.xAxisLabel.replace(/ Score$/, '').replace(/ Rating$/, '')
      var yShort = this.yAxisLabel.replace(/ Score$/, '').replace(/ Rating$/, '')
      return {
        tl: 'Low ' + xShort + ', High ' + yShort,
        tr: 'High ' + xShort + ', High ' + yShort,
        bl: 'Low ' + xShort + ', Low ' + yShort,
        br: 'High ' + xShort + ', Low ' + yShort,
      }
    },

    drawerTitle() {
      if (this.drawerEmployee) return this.drawerEmployee.name
      return 'Employee Detail'
    },

    legendItems() {
      if (this.colorBy === 'none') return []

      var items = []
      var seen = {}

      if (this.colorBy === 'talent_type') {
        this.matrixRows.forEach(function (row) {
          var t = row.talent_type || 'Unknown'
          if (!seen[t]) {
            seen[t] = true
            items.push({ label: t, color: TALENT_COLORS[t] || '#999' })
          }
        })
      } else if (this.colorBy === 'team') {
        var teamColorMap = {}
        var colorIdx = 0
        this.matrixRows.forEach(function (row) {
          var t = row.team_name || row.team || 'Unknown'
          if (!seen[t]) {
            seen[t] = true
            if (!teamColorMap[t]) {
              teamColorMap[t] = TEAM_COLORS[colorIdx % TEAM_COLORS.length]
              colorIdx++
            }
            items.push({ label: t, color: teamColorMap[t] })
          }
        })
      }

      return items
    },

    powerSkillKeys() {
      return AXIS_OPTIONS.filter(function (o) { return o.key.startsWith('ps_') }).map(function (o) {
        return { key: o.key, label: o.label.replace(' (EQ)', '') }
      })
    },
  },

  watch: {
    user: {
      deep: true,
      handler: function () { this.ensureBaseDataLoaded() }
    },
    matrixRows: function () { this.updateChart() },
    allEmployees: function () { this.ensureBaseDataLoaded() },
    allGoalsFlat: function () {
      if (this.hasAnyGoalMetricSelected()) this.updateChart()
    },
    compositeScores: function () {
      if (this.hasAnyCompositeMetricSelected()) this.updateChart()
    },
    colorBy: function () { this.updateChart() },
  },

  async mounted() {
    this.syncFromStore()
    await this.ensureTeamOptionsLoaded()
    await this.ensureBaseDataLoaded()
    // Chart creation handled by watchers reacting to data load
    // Only create if watchers haven't already
    if (!this.chart && this.selectedMetricDataReady()) {
      this.createChart()
    }
  },

  beforeDestroy() {
    this.cancelPendingChartUpdate()
    if (this.chart) this.chart.destroy()
  },

  methods: {
    getRowKey(row) {
      return row && row.rowKey ? row.rowKey : this.getRowKeyFromEmployee(row || {}, 0)
    },

    getRowKeyFromEmployee(emp, index) {
      return (emp && (emp.email || emp.key || emp.id)) || ('employee-' + index)
    },

    syncFromStore() {
      var state = this.$store.state.talentMatrix
      if (state) {
        this.localSelectedTeam = state.selectedTeam || ''
        this.localSelectedXAxis = state.selectedXAxis || 'impact_rating'
        this.localSelectedYAxis = state.selectedYAxis || 'healthbar'
        this.localColorBy = state.colorBy || 'talent_type'
      }
    },

    hasLoadedCompanyData() {
      var user = this.user
      var talentMatrixState = this.$store.state.talentMatrix || {}
      var hasEmployees = Array.isArray(this.allEmployees) && this.allEmployees.length > 0

      if (!user || !user.company) return false
      if (!hasEmployees) return false

      if (this.hasAnyGoalMetricSelected() && !this.hasLoadedGoalMetrics()) return false

      if (this.hasAnyCompositeMetricSelected()) {
        if (!talentMatrixState.compositeLoaded) return false
        if (talentMatrixState.compositeCompany !== user.company) return false
        if (this.requiresWho5Metric() && !(talentMatrixState.who5Loaded && talentMatrixState.who5ScoresCompany === user.company)) return false
        if (this.requiresDailyCheckinMetric() && !talentMatrixState.dailyCheckinsLoaded) return false
      }

      return true
    },

    metricNeedsCompositeData(key) {
      return !!key && key.indexOf('computed_') === 0
    },

    metricNeedsGoalsData(key) {
      return GOAL_AXIS_KEYS.indexOf(key) >= 0
    },

    hasAnyCompositeMetricSelected() {
      return this.metricNeedsCompositeData(this.selectedXAxis) || this.metricNeedsCompositeData(this.selectedYAxis)
    },

    hasAnyGoalMetricSelected() {
      return this.metricNeedsGoalsData(this.selectedXAxis) || this.metricNeedsGoalsData(this.selectedYAxis)
    },

    hasLoadedGoalMetrics() {
      var user = this.user
      return !!(user && user.company && this.goalMetricsLoadedCompany === user.company)
    },

    isMetricDataReady(key) {
      var talentMatrixState = this.$store.state.talentMatrix || {}
      var user = this.user

      if (this.metricNeedsGoalsData(key)) {
        return this.hasLoadedGoalMetrics()
      }
      if (!this.metricNeedsCompositeData(key)) return true
      if (!user || !user.company) return false
      if (!talentMatrixState.compositeLoaded) return false
      if (talentMatrixState.compositeCompany !== user.company) return false
      if (key === 'computed_who5' && !(talentMatrixState.who5Loaded && talentMatrixState.who5ScoresCompany === user.company)) return false
      if ((key === 'computed_checkin_consistency' || key === 'computed_flight_risk') && !talentMatrixState.dailyCheckinsLoaded) return false

      return true
    },

    selectedMetricDataReady() {
      return this.isMetricDataReady(this.selectedXAxis) && this.isMetricDataReady(this.selectedYAxis)
    },

    async ensureTeamOptionsLoaded() {
      var user = this.user
      if (!user || !user.company) return
      if (this.teamOptions.length > 0) return

      await this.loadTeamOptions(user.company)
    },

    async ensureBaseDataLoaded() {
      var user = this.user

      if (!user || !user.company) return
      await this.ensureTeamOptionsLoaded()
      if (this.baseLoadInFlight) {
        this.pendingBaseReload = true
        return
      }
      if (this.hasLoadedCompanyData()) return

      this.baseLoadInFlight = true
      try {
        await this.loadData()
      } finally {
        this.baseLoadInFlight = false
        if (!this._isDestroyed && this.pendingBaseReload) {
          this.pendingBaseReload = false
          await this.ensureBaseDataLoaded()
        }
      }
    },

    async loadData() {
      var user = this.user
      if (!user || !user.company) return

      var teamPromise = this.loadTeamOptions(user.company)

      await this.$store.dispatch('talentMatrix/loadMatrixData', user)
      await teamPromise

      if (this.hasAnyGoalMetricSelected()) {
        await this.ensureGoalMetricDataLoaded()
      }

      if (this.hasAnyCompositeMetricSelected()) {
        // Only block on composite metrics when the current axes actually need them.
        await this.loadCompositeScores({
          includeWho5: this.requiresWho5Metric(),
          includeDailyCheckins: this.requiresDailyCheckinMetric(),
        })
      }
    },

    requiresWho5Metric() {
      return this.selectedXAxis === 'computed_who5' || this.selectedYAxis === 'computed_who5'
    },

    requiresDailyCheckinMetric() {
      return this.selectedXAxis === 'computed_checkin_consistency' ||
        this.selectedYAxis === 'computed_checkin_consistency' ||
        this.selectedXAxis === 'computed_flight_risk' ||
        this.selectedYAxis === 'computed_flight_risk'
    },

    async ensureSelectedMetricData() {
      var user = this.user
      var talentMatrixState = this.$store.state.talentMatrix || {}
      if (!user || !user.company || this.optionalMetricLoadInFlight) return

      if (!this.requiresWho5Metric() && !this.requiresDailyCheckinMetric()) return
      if (
        talentMatrixState.compositeCompany === user.company &&
        (!this.requiresWho5Metric() || (talentMatrixState.who5Loaded && talentMatrixState.who5ScoresCompany === user.company)) &&
        (!this.requiresDailyCheckinMetric() || talentMatrixState.dailyCheckinsLoaded)
      ) return

      this.optionalMetricLoadInFlight = true
      try {
        await this.loadCompositeScores({
          includeWho5: this.requiresWho5Metric(),
          includeDailyCheckins: this.requiresDailyCheckinMetric(),
        })
      } finally {
        this.optionalMetricLoadInFlight = false
      }
    },

    async ensureGoalMetricDataLoaded() {
      var user = this.user
      if (!user || !user.company || this.goalMetricLoadInFlight) return
      if (!this.hasAnyGoalMetricSelected()) return
      if (this.hasLoadedGoalMetrics()) return

      this.goalMetricLoadInFlight = true
      try {
        await this.$store.dispatch('goals/getGoalsTeamAI')
        this.goalMetricsLoadedCompany = user.company
        this.updateChart()
      } catch (error) {
        console.error('Error loading goals data for talent matrix:', error)
      } finally {
        this.goalMetricLoadInFlight = false
      }
    },

    async loadCompositeScores(options) {
      var user = this.user
      if (!user || !user.company) return

      await this.$store.dispatch('talentMatrix/loadCompositeScores', Object.assign({ user: user }, options || {}))
    },

    doesTeamSourceMatchCompany(teams, company) {
      var firstTeam = null

      if (Array.isArray(teams)) {
        firstTeam = teams.find(function (team) { return !!team })
      } else if (teams) {
        var teamKeys = Object.keys(teams)
        firstTeam = teamKeys.length > 0 ? teams[teamKeys[0]] : null
      }

      if (!firstTeam || !firstTeam.company) return true
      return firstTeam.company === company
    },

    getCachedTeams(company) {
      var mainTeams = this.$store.state.main && this.$store.state.main.allTeam
      if (mainTeams && Object.keys(mainTeams).length > 0 && this.doesTeamSourceMatchCompany(mainTeams, company)) {
        return mainTeams
      }

      var teamModuleTeams = this.$store.state.team && this.$store.state.team.teamsall
      if (teamModuleTeams && teamModuleTeams.length > 0 && this.doesTeamSourceMatchCompany(teamModuleTeams, company)) {
        return teamModuleTeams
      }

      return null
    },

    setTeamOptions(teams) {
      var options = []

      if (Array.isArray(teams)) {
        teams.forEach(function (team) {
          if (team && team.key && team.name) {
            options.push({ key: team.key, name: team.name, parent: team.parent || null })
          }
        })
      } else if (teams) {
        Object.keys(teams).forEach(function (key) {
          if (teams[key] && teams[key].name) {
            options.push({ key: key, name: teams[key].name, parent: teams[key].parent || null })
          }
        })
      }

      options.sort(function (a, b) {
        return a.name.localeCompare(b.name)
      })

      this.teamOptions = options
      this.allTeamsCount = options.length

      if (this.localSelectedTeam && !options.some(function (option) { return option.key === this.localSelectedTeam }.bind(this))) {
        this.localSelectedTeam = ''
        this.$store.commit('talentMatrix/setSelectedTeam', '')
      }
    },

    async loadTeamOptions(company) {
      var cachedTeams = this.getCachedTeams(company)
      if (cachedTeams) {
        this.setTeamOptions(cachedTeams)
        return cachedTeams
      }

      var teams = await getTeamByCompany(company)
      this.setTeamOptions(teams)

      var mainState = this.$store.state.main
      if (teams && mainState && (!mainState.allTeam || Object.keys(mainState.allTeam).length === 0 || !this.doesTeamSourceMatchCompany(mainState.allTeam, company))) {
        this.$store.commit('main/setAllTeam', teams)
      }

      return teams
    },

    getMetricValue(emp, key) {
      if (this.metricNeedsGoalsData(key)) {
        var goalMetrics = this.goalMetricsByEmail[emp.email]
        return goalMetrics ? (goalMetrics[key] || 0) : 0
      }
      if (key.startsWith('computed_')) {
        var talentMatrixState = this.$store.state.talentMatrix || {}
        if (key === 'computed_flight_risk' && !talentMatrixState.dailyCheckinsLoaded) return null
        var compositeScores = talentMatrixState.compositeScores
        if (compositeScores && emp.email && compositeScores[emp.email]) {
          var scoreValue = compositeScores[emp.email][key]
          if (scoreValue === null || scoreValue === undefined || scoreValue === '') {
            return key === 'computed_flight_risk' ? null : 0
          }
          return scoreValue || 0
        }
        return key === 'computed_flight_risk' ? null : 0
      }
      if (key.startsWith('ps_')) {
        if (emp.power_tag && emp.power_tag[key]) {
          var tag = emp.power_tag[key]
          return tag.point_total > 0 ? Math.round((tag.point / tag.point_total) * 100) / 10 : 0
        }
        return 0
      }
      var raw = emp[key]
      if (raw == null) return 0
      var num = parseFloat(raw)
      return isNaN(num) ? 0 : num
    },

    isMissingMetricValue(value) {
      return value === null || value === undefined || value === '' || isNaN(Number(value))
    },

    getMetricDisplayValue(value, key, suffix) {
      if (this.isMissingMetricValue(value)) return 'No data'
      var metricSuffix = suffix !== undefined ? suffix : (this.isPercentMetric(key) ? '%' : '')
      return '' + value + metricSuffix
    },

    getMetricDisplayForEmployee(emp, key, suffix) {
      return this.getMetricDisplayValue(this.getMetricValue(emp, key), key, suffix)
    },

    computeAxisRange(metricKey, values) {
      var config = AXIS_OPTIONS.find(function (o) { return o.key === metricKey })
      if (!config) return { min: -2, max: 102 }
      if (!config.dynamic) {
        // Add edge padding so dots at min/max don't get clipped
        // For 0-100 scales, use 0 to ~103 so axis labels stay clean
        var span = config.max - config.min
        var edgePad = Math.max(span * 0.03, 1)
        return { min: config.min, max: config.max + edgePad }
      }
      values = Array.isArray(values) ? values : []
      if (values.length === 0) {
        var s = config.max - config.min
        var ep = s * 0.03 || 1
        return { min: config.min - ep, max: config.max + ep }
      }
      var dataMin = values[0]
      var dataMax = values[0]
      for (var i = 1; i < values.length; i++) {
        if (values[i] < dataMin) dataMin = values[i]
        if (values[i] > dataMax) dataMax = values[i]
      }
      var padding = (dataMax - dataMin) * 0.08 || 2
      return {
        min: Math.floor(dataMin - padding),
        max: Math.ceil(dataMax + padding)
      }
    },

    getQuadrantKeyFromValues(xVal, yVal, xRange, yRange) {
      var xMid = (xRange.min + xRange.max) / 2
      var yMid = (yRange.min + yRange.max) / 2
      var highX = xVal >= xMid
      var highY = yVal >= yMid
      if (highX && highY) return 'tr'
      if (!highX && highY) return 'tl'
      if (highX && !highY) return 'br'
      return 'bl'
    },

    getQuadrantKey(emp) {
      var xVal = this.getMetricValue(emp, this.selectedXAxis)
      var yVal = this.getMetricValue(emp, this.selectedYAxis)
      return this.getQuadrantKeyFromValues(xVal, yVal, this.xAxisRange, this.yAxisRange)
    },

    getQuadrantLabel(emp) {
      var key = this.getQuadrantKey(emp)
      return this.quadrantLabels[key] || ''
    },

    getQuadrantClass(emp) {
      var key = this.getQuadrantKey(emp)
      return this.quadrantTagClass(key)
    },

    quadrantSentimentFor(quadKey) {
      // Returns: 3=critical, 2=success, 1=warning, 0=danger
      // Critical = valuable person with a risk signal (most urgent to act on)
      var xCfg = this.xAxisConfig || {}
      var yCfg = this.yAxisConfig || {}
      var xHighGood = xCfg.highIsGood !== false
      var yHighGood = yCfg.highIsGood !== false
      var highX = quadKey === 'tr' || quadKey === 'br'
      var highY = quadKey === 'tl' || quadKey === 'tr'
      var xGood = highX ? xHighGood : !xHighGood
      var yGood = highY ? yHighGood : !yHighGood

      if (xGood && yGood) return 2     // both good → success
      if (!xGood && !yGood) return 0   // both bad → danger

      // Mixed: one good, one bad. Determine urgency.
      // Critical = the good dimension shows strength (highIsGood=true and value is high)
      //            AND the bad dimension is a negative/risk metric showing a problem.
      // Warning  = merely underperforming on a positive metric, no active risk signal.
      var hasRiskSignal = (!xGood && !xHighGood) || (!yGood && !yHighGood)
      var hasStrength = (xGood && xHighGood) || (yGood && yHighGood)
      if (hasRiskSignal && hasStrength) return 3  // valuable person at risk → critical
      return 1                                     // underperforming but no risk → warning
    },

    quadrantPillClass(quadKey) {
      var sent = this.quadrantSentimentFor(quadKey)
      return { 3: 'pill-critical', 2: 'pill-success', 1: 'pill-warning', 0: 'pill-danger' }[sent] || 'pill-neutral'
    },

    quadrantTagClass(quadKey) {
      var sent = this.quadrantSentimentFor(quadKey)
      return { 3: 'qt-critical', 2: 'qt-success', 1: 'qt-warning', 0: 'qt-danger' }[sent] || 'qt-neutral'
    },

    handleTeamChange(val) {
      this.$store.commit('talentMatrix/setSelectedTeam', val)
    },

    async handleXAxisChange(val) {
      this.$store.commit('talentMatrix/setSelectedXAxis', val)
      await this.ensureBaseDataLoaded()
      this.updateChart()
    },

    async handleYAxisChange(val) {
      this.$store.commit('talentMatrix/setSelectedYAxis', val)
      await this.ensureBaseDataLoaded()
      this.updateChart()
    },

    handleColorByChange(val) {
      this.$store.commit('talentMatrix/setColorBy', val)
    },

    async swapAxes() {
      var x = this.localSelectedXAxis
      var y = this.localSelectedYAxis
      this.localSelectedXAxis = y
      this.localSelectedYAxis = x
      this.$store.commit('talentMatrix/setSelectedXAxis', y)
      this.$store.commit('talentMatrix/setSelectedYAxis', x)
      await this.ensureBaseDataLoaded()
      this.updateChart()
    },

    isPercentMetric(key) {
      return key === 'computed_flight_risk' ||
        key === 'computed_checkin_consistency' ||
        key === 'computed_manager_responsiveness' ||
        key === 'computed_performance' ||
        key === 'computed_goal_alignment' ||
        key === 'kr_progress' ||
        key === 'kr_health'
    },

    getScoreColor(value, max, invert) {
      if (this.isMissingMetricValue(value)) return '#D1D5DB'
      var pct = max > 0 ? value / max : 0
      if (invert) pct = 1 - pct
      if (pct >= 0.7) return '#2E7D32'
      if (pct >= 0.4) return '#F9A825'
      return '#CC3030'
    },

    getBarWidth(emp, key, max) {
      var val = this.getMetricValue(emp, key)
      if (this.isMissingMetricValue(val)) return 0
      if (!max) return 0
      return Math.min(100, Math.round((val / max) * 100))
    },

    getTalentColor(talent) {
      return TALENT_COLORS[talent] || '#999'
    },

    badgeStyle(color) {
      return { background: color + '0F', color: color }
    },

    getTalentBadgeStyle(talent) {
      return this.badgeStyle(this.getTalentColor(talent))
    },

    getNetworkColor(networkType) {
      var t = (networkType || '').toLowerCase()
      if (t.indexOf('super') >= 0) return '#1B5E20'
      if (t.indexOf('high') >= 0) return '#00897B'
      if (t.indexOf('medium') >= 0) return '#F9A825'
      return '#909399'
    },

    getNetworkBadgeStyle(networkType) {
      return this.badgeStyle(this.getNetworkColor(networkType))
    },

    getMetricPillStyle(value, axisKey) {
      var config = AXIS_OPTIONS.find(function (o) { return o.key === axisKey })
      if (!config) return {}
      if (this.isMissingMetricValue(value)) return this.badgeStyle('#909399')
      // Impact Rating uses absolute thresholds (matching people.vue)
      if (axisKey === 'impact_rating') {
        var impactColor = value >= 70 ? '#2E7D32' : value >= 40 ? '#F9A825' : '#CC3030'
        return this.badgeStyle(impactColor)
      }
      return this.badgeStyle(this.getScoreColor(value, config.max || 100, !config.highIsGood))
    },

    getDisplayName(emp) {
      return emp.fullname || ((emp.firstname || '') + ' ' + (emp.lastname || '')).trim() || emp.email || ''
    },

    getInitials(name) {
      if (!name) return '?'
      var parts = name.trim().split(/\s+/)
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
      return name[0].toUpperCase()
    },

    isDiscreteMetric(key) {
      return key === 'risk_level' || key === 'star'
    },

    getDeterministicJitter(seed) {
      var hash = 0
      seed = String(seed || '')
      for (var i = 0; i < seed.length; i++) {
        hash = ((hash << 5) - hash) + seed.charCodeAt(i)
        hash |= 0
      }
      return (((Math.abs(hash) % 1000) / 1000) - 0.5) * 0.3
    },

    getChartValueFromValue(value, key, rowKey) {
      var val = value
      if (this.isDiscreteMetric(key)) {
        val += this.getDeterministicJitter(rowKey + '-' + key)
      }
      return val
    },

    getChartValue(emp, key) {
      return this.getChartValueFromValue(this.getMetricValue(emp, key), key, this.getRowKeyFromEmployee(emp, 0))
    },

    buildDatasets() {
      var rows = this.matrixRows

      if (!this.selectedMetricDataReady()) {
        return []
      }

      if (this.colorBy === 'none') {
        return [{
          label: 'Employees',
          data: rows.map(function (row) {
            return {
              x: row.chartX,
              y: row.chartY,
              row: row,
            }
          }),
          backgroundColor: 'rgba(108, 48, 204, 0.6)',
          borderColor: 'rgba(108, 48, 204, 0.8)',
          pointRadius: 7,
          pointHoverRadius: 10,
        }]
      }

      var groups = {}
      var groupColors = {}
      var teamColorIdx = 0

      rows.forEach(function (row) {
        var groupKey
        if (this.colorBy === 'talent_type') {
          groupKey = row.talent_type || 'Unknown'
          groupColors[groupKey] = TALENT_COLORS[groupKey] || '#999'
        } else {
          groupKey = row.team_name || row.team || 'Unknown'
          if (!groupColors[groupKey]) {
            groupColors[groupKey] = TEAM_COLORS[teamColorIdx % TEAM_COLORS.length]
            teamColorIdx++
          }
        }
        if (!groups[groupKey]) groups[groupKey] = []
        groups[groupKey].push({
          x: row.chartX,
          y: row.chartY,
          row: row,
        })
      }.bind(this))

      return Object.keys(groups).map(function (key) {
        var color = groupColors[key]
        return {
          label: key,
          data: groups[key],
          backgroundColor: color + '99',
          borderColor: color,
          pointRadius: 7,
          pointHoverRadius: 10,
        }
      })
    },

    getChartAxisState() {
      var xCfgBase = this.xAxisConfig || { min: 0, max: 100, label: '' }
      var yCfgBase = this.yAxisConfig || { min: 0, max: 100, label: '' }
      var xCfg = { min: this.xAxisRange.min, max: this.xAxisRange.max, label: xCfgBase.label }
      var yCfg = { min: this.yAxisRange.min, max: this.yAxisRange.max, label: yCfgBase.label }

      return {
        xCfgBase: xCfgBase,
        yCfgBase: yCfgBase,
        xCfg: xCfg,
        yCfg: yCfg,
        xMid: (xCfg.min + xCfg.max) / 2,
        yMid: (yCfg.min + yCfg.max) / 2,
      }
    },

    buildQuadrantPluginOptions(axisState) {
      return {
        xMid: axisState.xMid,
        yMid: axisState.yMid,
        labels: this.quadrantLabels,
        sentiments: {
          tl: this.quadrantSentimentFor('tl'),
          tr: this.quadrantSentimentFor('tr'),
          bl: this.quadrantSentimentFor('bl'),
          br: this.quadrantSentimentFor('br'),
        },
      }
    },

    buildScaleOptions(axis, cfg, cfgBase) {
      return {
        min: cfg.min,
        max: cfg.max,
        title: {
          display: true,
          text: cfg.label,
          font: { size: 12, weight: '600', family: 'Lato' },
          color: '#5a5a7a',
          padding: axis === 'x' ? { top: 8 } : { bottom: 8 },
        },
        grid: {
          color: 'rgba(232, 232, 240, 0.5)',
          lineWidth: 1,
        },
        ticks: {
          font: { size: 11, family: 'Lato' },
          color: '#8e8ea0',
          callback: function (value) {
            if (value < 0) return ''
            if (value > cfgBase.max) return ''
            return value
          },
        },
      }
    },

    handleExternalTooltip(context) {
      var tooltipModel = context.tooltip
      if (tooltipModel.opacity === 0) {
        this.tooltipVisible = false
        return
      }
      if (!tooltipModel.dataPoints || tooltipModel.dataPoints.length === 0) return

      var dp = tooltipModel.dataPoints[0]
      var raw = dp.raw
      var row = raw && (raw.row || raw.emp)
      if (!row) return

      this.tooltipData = {
        name: row.name || this.getDisplayName(row),
        team_name: row.team_name || '',
        talent_type: row.talent_type || row.talent || '',
        pic: row.pic || row.profilepic_small || '',
        xVal: row.xVal,
        yVal: row.yVal,
      }

      var canvasRect = context.chart.canvas.getBoundingClientRect()
      var tooltipW = 220
      var tooltipH = 100
      var caretX = tooltipModel.caretX
      var caretY = tooltipModel.caretY
      var leftPos = caretX + 14
      var topPos = caretY - 14

      if (caretX + tooltipW + 20 > canvasRect.width) {
        leftPos = caretX - tooltipW - 14
      }
      if (caretY + tooltipH > canvasRect.height) {
        topPos = caretY - tooltipH - 14
      }
      if (topPos < 0) topPos = 4

      this.tooltipStyle = {
        left: leftPos + 'px',
        top: topPos + 'px',
      }
      this.tooltipVisible = true
    },

    handleChartClick(event, elements) {
      if (!this.chart || !elements || elements.length === 0) return

      var el = elements[0]
      var raw = this.chart.data.datasets[el.datasetIndex].data[el.index]
      var row = raw && (raw.row || raw.emp)
      if (row) this.openDrawer(row)
    },

    buildChartOptions() {
      var self = this
      var axisState = this.getChartAxisState()

      return {
        responsive: true,
        maintainAspectRatio: false,
        clip: false,
        animation: { duration: 0 },
        layout: {
          padding: { top: 10, right: 10, bottom: 5, left: 5 }
        },
        plugins: {
          legend: { display: false },
          quadrantBackgrounds: this.buildQuadrantPluginOptions(axisState),
          tooltip: {
            enabled: false,
            external: function (context) {
              self.handleExternalTooltip(context)
            },
          },
        },
        onClick: function (event, elements) {
          self.handleChartClick(event, elements)
        },
        scales: {
          x: this.buildScaleOptions('x', axisState.xCfg, axisState.xCfgBase),
          y: this.buildScaleOptions('y', axisState.yCfg, axisState.yCfgBase),
        },
      }
    },

    createChart() {
      var canvas = this.$refs.talentMatrixChart
      if (!canvas || !this.selectedMetricDataReady()) return

      if (this.chart) {
        this.refreshChart()
        return
      }

      this.chart = new Chart(canvas, {
        type: 'scatter',
        data: {
          datasets: this.buildDatasets(),
        },
        plugins: [QUADRANT_PLUGIN],
        options: this.buildChartOptions(),
      })
    },

    refreshChart() {
      if (!this.chart) {
        this.createChart()
        return
      }

      if (!this.selectedMetricDataReady()) return

      this.chart.data.datasets = this.buildDatasets()
      this.chart.options = this.buildChartOptions()
      this.chart.update('none')
    },

    requestChartFrame(callback) {
      if (typeof window !== 'undefined' && window.requestAnimationFrame) {
        this.chartUpdateFrameType = 'raf'
        return window.requestAnimationFrame(callback)
      }
      this.chartUpdateFrameType = 'timeout'
      return setTimeout(callback, 0)
    },

    cancelPendingChartUpdate() {
      if (!this.chartUpdateFrame) return
      if (this.chartUpdateFrameType === 'raf' && typeof window !== 'undefined' && window.cancelAnimationFrame) {
        window.cancelAnimationFrame(this.chartUpdateFrame)
      } else {
        clearTimeout(this.chartUpdateFrame)
      }
      this.chartUpdateFrame = null
      this.chartUpdateFrameType = null
      this.chartUpdateQueued = false
    },

    updateChart() {
      if (this.chartUpdateQueued) return
      this.chartUpdateQueued = true

      this.$nextTick(function () {
        if (this._isDestroyed) {
          this.chartUpdateQueued = false
          return
        }

        this.chartUpdateFrame = this.requestChartFrame(function () {
          this.chartUpdateFrame = null
          this.chartUpdateFrameType = null
          this.chartUpdateQueued = false

          if (this._isDestroyed || !this.selectedMetricDataReady()) return
          if (this.chart) {
            this.refreshChart()
          } else {
            this.createChart()
          }
        }.bind(this))
      }.bind(this))
    },

    handleRowClick(row) {
      this.openDrawer(row)
    },

    openDrawer(emp) {
      this.drawerEmployee = emp
      this.drawerVisible = true
    },

    handleSortChange(sort) {
      this.sortProp = sort.prop
      this.sortOrder = sort.order
    },

    exportCSV() {
      var rows = [
        ['Employee', 'Email', this.xAxisLabel, this.yAxisLabel, 'Team', 'Network', 'Talent Type', 'Quadrant'],
      ]
      this.tableData.forEach(function (row) {
        rows.push([
          '"' + (row.name || '').replace(/"/g, '""') + '"',
          '"' + (row.email || '').replace(/"/g, '""') + '"',
          row.xVal,
          row.yVal,
          '"' + (row.team_name || '').replace(/"/g, '""') + '"',
          '"' + (row.network_type || '').replace(/"/g, '""') + '"',
          '"' + (row.talent_type || '').replace(/"/g, '""') + '"',
          '"' + (row.quadrant || '').replace(/"/g, '""') + '"',
        ])
      })

      var xShort = this.xAxisLabel.replace(/\s+/g, '-').toLowerCase()
      var yShort = this.yAxisLabel.replace(/\s+/g, '-').toLowerCase()
      var selectedTeam = this.selectedTeam
      var team = selectedTeam ? this.teamOptions.find(function (t) { return t.key === selectedTeam }) : null
      var teamSlug = team && team.name ? team.name.replace(/\s+/g, '-').toLowerCase() : 'all-teams'
      var filename = 'talent-matrix_' + teamSlug + '_' + xShort + '_vs_' + yShort + '.csv'

      var csv = rows.map(function (r) { return r.join(',') }).join('\n')
      var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      var link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = filename
      link.click()
      URL.revokeObjectURL(link.href)
    },
  },
}
</script>

<style scoped>
/* Header Controls - matches focus-areas.vue / goals.vue pattern */
.header-controls {
  padding: 24px 0 16px 0;
}

.header-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-row-primary {
  justify-content: space-between;
  margin-bottom: 12px;
}

.header-row-filters {
  gap: 8px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-left .team-select {
  width: 200px;
}

.axis-select {
  width: 180px;
}

.header-right .color-by-select {
  width: 180px;
}

.toggle-wrapper {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 0;
  white-space: nowrap;
}

.toggle-label {
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  user-select: none;
}

.btn-swap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(108, 48, 204, 0.06);
  border: 1.5px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #6C30CC;
}

.btn-swap:hover {
  background: #6C30CC;
  color: white;
  transform: rotate(90deg);
}

.icon-export {
  color: #909399;
  cursor: pointer;
  transition: color 0.2s;
}
.icon-export:hover {
  color: #6C30CC;
}

/* Dimension Info Bar */
.dimension-info-bar {
  display: flex;
  gap: 0;
  background: #f8f9fc;
  border: 1px solid #e8e8f0;
  border-radius: 8px;
  padding: 0;
  margin-bottom: 16px;
  overflow: hidden;
}
.dimension-info-item {
  flex: 1;
  padding: 14px 18px;
}
.dimension-info-divider {
  width: 1px;
  background: #e8e8f0;
  flex-shrink: 0;
}
.dimension-info-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}
.dimension-info-axis {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #fff;
  background: #6C30CC;
  padding: 2px 6px;
  border-radius: 3px;
  flex-shrink: 0;
}
.dimension-info-label {
  font-size: 13px;
  font-weight: 700;
  color: #2e2e2e;
}
.dimension-info-polarity {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 3px;
  flex-shrink: 0;
  margin-left: auto;
}
.polarity-positive {
  background: rgba(58, 196, 125, 0.1);
  color: #1a8a4a;
}
.polarity-negative {
  background: rgba(204, 48, 48, 0.08);
  color: #CC3030;
}
.dimension-info-desc {
  font-size: 12px;
  color: #4a4a5a;
  line-height: 1.4;
  margin-bottom: 4px;
}
.dimension-info-data {
  font-size: 11px;
  color: #909399;
  line-height: 1.4;
  font-style: italic;
}

/* Chart Wrapper */
.chart-wrapper {
  position: relative;
  height: 500px;
  margin-bottom: 8px;
}

/* Matrix Loading Indicator */
.matrix-loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.85);
  z-index: 10;
}
.matrix-loader {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  width: 48px;
  height: 48px;
}
.matrix-cell {
  border-radius: 4px;
  animation: matrixPulse 1.2s ease-in-out infinite;
}
.cell-tl { background: #6C30CC; animation-delay: 0s; }
.cell-tr { background: #3AC47D; animation-delay: 0.15s; }
.cell-bl { background: #F9A825; animation-delay: 0.3s; }
.cell-br { background: #CC3030; animation-delay: 0.45s; }
@keyframes matrixPulse {
  0%, 100% { opacity: 0.25; transform: scale(0.85); }
  40% { opacity: 1; transform: scale(1); }
}

/* Custom Tooltip */
.chart-tooltip {
  position: absolute;
  pointer-events: none;
  background: #ffffff;
  border: 1px solid #e8e8f0;
  border-radius: 10px;
  box-shadow: 0 8px 32px rgba(108, 48, 204, 0.08), 0 4px 8px rgba(0, 0, 0, 0.03);
  padding: 12px 16px;
  z-index: 100;
  opacity: 0;
  transition: opacity 0.15s ease;
  min-width: 180px;
}
.chart-tooltip.visible { opacity: 1; }

.tooltip-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.tooltip-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(108, 48, 204, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #6C30CC;
  flex-shrink: 0;
  overflow: hidden;
}
.tooltip-avatar img { width: 100%; height: 100%; object-fit: cover; }
.tooltip-name {
  font-size: 13px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 1px;
}
.tooltip-team {
  font-size: 11px;
  color: #8e8ea0;
}
.tooltip-metrics {
  display: flex;
  gap: 16px;
}
.tooltip-metric {
  display: flex;
  flex-direction: column;
}
.tooltip-metric-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8e8ea0;
}
.tooltip-metric-val {
  font-size: 16px;
  font-weight: 900;
  color: #6C30CC;
}

/* Legend */
.chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 16px;
  padding: 12px 0 4px;
  margin-bottom: 20px;
}
.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: #5a5a7a;
  padding: 3px 6px;
  border-radius: 4px;
  transition: all 0.15s ease;
}
.legend-item:hover { background: #fafaff; }
.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* Quadrant Summary Pills */
.quadrant-summary {
  display: flex;
  gap: 12px;
  margin-bottom: 28px;
}
.q-pill {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 600;
  cursor: default;
  min-width: 0;
}
.q-pill .q-count {
  font-size: 14px;
  font-weight: 900;
  line-height: 1;
  flex-shrink: 0;
}
.pill-critical { background: rgba(183, 28, 28, 0.08); color: #b71c1c; }
.pill-danger { background: rgba(204, 48, 48, 0.06); color: #CC3030; }
.pill-warning { background: rgba(247, 185, 36, 0.07); color: #b8860b; }
.pill-success { background: rgba(58, 196, 125, 0.07); color: #1a8a4a; }
.pill-neutral { background: rgba(108, 48, 204, 0.06); color: #8e8ea0; }

/* Table Section */
.table-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}
.table-title {
  font-size: 14px;
  font-weight: 700;
  color: #2e2e2e;
}
.table-count {
  font-size: 12px;
  color: #909399;
  font-weight: 500;
}

/* Table styling to match insights views */
.talent-table {
  border-radius: 4px;
  overflow: hidden;
  box-shadow:
    0px 0.3px 0.9px rgba(0, 0, 0, 0.008),
    0px 0.8px 2.1px rgba(0, 0, 0, 0.012),
    0px 1.5px 4px rgba(0, 0, 0, 0.015);
}

.emp-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 0;
}
.emp-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(108, 48, 204, 0.06);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  color: #6C30CC;
  flex-shrink: 0;
  overflow: hidden;
}
.emp-avatar img { width: 100%; height: 100%; object-fit: cover; }
.emp-info {
  min-width: 0;
}
.emp-name {
  font-size: 13px;
  font-weight: 600;
  color: #2e2e2e;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
}
.emp-team-small {
  font-size: 11px;
  color: #909399;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
}

.metric-pill {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.network-badge,
.talent-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.quadrant-tag {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
  text-align: center;
}
.qt-critical { background: rgba(183, 28, 28, 0.08); color: #b71c1c; }
.qt-danger { background: rgba(204, 48, 48, 0.06); color: #CC3030; }
.qt-warning { background: rgba(247, 185, 36, 0.07); color: #b8860b; }
.qt-success { background: rgba(58, 196, 125, 0.07); color: #1a8a4a; }
.qt-neutral { background: rgba(108, 48, 204, 0.06); color: #8e8ea0; }

/* Drawer Styles */
.drawer-content {
  height: 100%;
  overflow-y: auto;
}
.drawer-profile {
  padding: 28px 24px 20px;
  border-bottom: 1px solid #f0f0f6;
}
.drawer-profile-top {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}
.drawer-avatar-ring {
  flex-shrink: 0;
  padding: 3px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6C30CC 0%, #3AC47D 100%);
}
.drawer-avatar {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  background: #f5f5fa;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  font-weight: 700;
  color: #6C30CC;
  overflow: hidden;
  border: 3px solid #fff;
}
.drawer-avatar img { width: 100%; height: 100%; object-fit: cover; }
.drawer-profile-info {
  flex: 1;
  min-width: 0;
  padding-top: 4px;
}
.drawer-name {
  font-size: 18px;
  font-weight: 800;
  color: #1a1a2e;
  margin-bottom: 2px;
  line-height: 1.3;
}
.drawer-meta {
  font-size: 13px;
  color: #909399;
  font-weight: 500;
  margin-bottom: 10px;
}
.drawer-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.drawer-section {
  padding: 18px 24px;
  border-bottom: 1px solid #f0f0f6;
}
.drawer-section:last-child { border-bottom: none; }
.drawer-section-title {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #909399;
  margin-bottom: 12px;
}
.metric-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 0;
}
.metric-row + .metric-row { border-top: 1px solid #f5f5f8; }
.metric-label {
  font-size: 12px;
  color: #606266;
  font-weight: 500;
  white-space: nowrap;
  min-width: 120px;
}
.metric-value {
  font-size: 13px;
  font-weight: 700;
  color: #2e2e2e;
  font-variant-numeric: tabular-nums;
  min-width: 36px;
  text-align: right;
}
.metric-bar-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  margin-left: 16px;
  max-width: 180px;
}
.metric-bar-track {
  flex: 1;
  height: 5px;
  background: #eeeef2;
  border-radius: 3px;
  overflow: hidden;
}
.metric-bar-fill {
  height: 100%;
  border-radius: 3px;
  background: #6C30CC;
  transition: width 0.5s cubic-bezier(0.16, 1, 0.3, 1);
}
</style>

<style>
/* Unscoped Element UI table overrides for talent matrix */
.talent-table .el-table__row {
  cursor: pointer;
  transition: background-color 0.2s;
}
.talent-table .el-table__row:hover > td {
  background-color: #fafafa !important;
}
.talent-table .el-table__body-wrapper {
  overflow-y: auto;
}
.talent-table .el-table__body-wrapper::-webkit-scrollbar {
  width: 8px;
}
.talent-table .el-table__body-wrapper::-webkit-scrollbar-thumb {
  background: #E0E0E0;
  border-radius: 4px;
}
.talent-table .el-table__body-wrapper::-webkit-scrollbar-track {
  background: transparent;
}
.talent-table.el-table--striped .el-table__body tr.el-table__row--striped td {
  background: #fafcff;
}
</style>
